import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Video, Plus, Trash2, Pencil, Check, X, Loader2, Youtube } from "lucide-react";
import { toast } from "sonner";

// Per-lesson "question → YouTube video" manager. Students pick a question from a
// dropdown (or ask it aloud) in the tutor and the matching video plays.
export default function QAVideoManager({ lessonId, lessonTitle, count = 0, onChange }) {
  const [open, setOpen] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editQ, setEditQ] = useState("");
  const [editUrl, setEditUrl] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tuition/library/lessons/${lessonId}/qa-videos`);
      setVideos(data.videos || []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load videos");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const add = async () => {
    const question = q.trim();
    const video_url = url.trim();
    if (!question) { toast.error("Enter the question."); return; }
    if (!/^https?:\/\//i.test(video_url)) { toast.error("Enter a valid YouTube link (https://…)."); return; }
    setSaving(true);
    try {
      await api.post(`/tuition/library/lessons/${lessonId}/qa-videos`, { question, video_url });
      setQ(""); setUrl("");
      await load();
      onChange && onChange();
      toast.success("Question added.");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not add");
    } finally { setSaving(false); }
  };

  const startEdit = (v) => { setEditId(v.id); setEditQ(v.question); setEditUrl(v.video_url); };
  const cancelEdit = () => { setEditId(null); setEditQ(""); setEditUrl(""); };

  const saveEdit = async () => {
    const question = editQ.trim();
    const video_url = editUrl.trim();
    if (!question || !/^https?:\/\//i.test(video_url)) { toast.error("Enter a question and a valid link."); return; }
    setSaving(true);
    try {
      await api.patch(`/tuition/library/lessons/${lessonId}/qa-videos/${editId}`, { question, video_url });
      cancelEdit();
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    setSaving(true);
    try {
      await api.delete(`/tuition/library/lessons/${lessonId}/qa-videos/${id}`);
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not delete");
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-8 rounded-full text-xs"
        onClick={() => setOpen(true)}
        data-testid={`qa-videos-btn-${lessonId}`}
      >
        <Video className="h-3.5 w-3.5 mr-1 text-rose-600" /> Q&amp;A Videos{count ? ` (${count})` : ""}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="qa-videos-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>Q&amp;A Videos</DialogTitle>
            <DialogDescription>
              Add questions and paste a YouTube link for each. Students pick a question (or ask it aloud) in <b>{lessonTitle}</b> and the matching video plays.
            </DialogDescription>
          </DialogHeader>

          {/* Add form */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Question (e.g. What is photosynthesis?)"
              data-testid="qa-video-question-input"
            />
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Youtube className="h-4 w-4 text-rose-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://youtu.be/…"
                  className="pl-8"
                  data-testid="qa-video-url-input"
                />
              </div>
              <Button onClick={add} disabled={saving} className="rounded-lg shrink-0 bg-sky-500 hover:bg-sky-600" data-testid="qa-video-add-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add</>}
              </Button>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto -mx-1 px-1 mt-2 divide-y divide-slate-100 dark:divide-slate-800">
            {loading ? (
              <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : videos.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400" data-testid="qa-videos-empty">No question videos yet. Add one above.</div>
            ) : videos.map((v, i) => (
              <div key={v.id} className="py-2.5" data-testid={`qa-video-row-${v.id}`}>
                {editId === v.id ? (
                  <div className="space-y-2">
                    <Input value={editQ} onChange={(e) => setEditQ(e.target.value)} data-testid="qa-video-edit-question" />
                    <div className="flex gap-2">
                      <Input value={editUrl} onChange={(e) => setEditUrl(e.target.value)} className="flex-1" data-testid="qa-video-edit-url" />
                      <Button size="icon" variant="outline" className="shrink-0" onClick={saveEdit} disabled={saving} data-testid="qa-video-edit-save"><Check className="h-4 w-4 text-emerald-600" /></Button>
                      <Button size="icon" variant="outline" className="shrink-0" onClick={cancelEdit} data-testid="qa-video-edit-cancel"><X className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{i + 1}. {v.question}</div>
                      <a href={v.video_url} target="_blank" rel="noreferrer" className="text-xs text-sky-600 hover:underline truncate block max-w-[320px]">{v.video_url}</a>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(v)} data-testid={`qa-video-edit-${v.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => del(v.id)} disabled={saving} data-testid={`qa-video-delete-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
